import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Globe, Clock } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { CountryGuidesLineNote } from "@/components/CountryGuidesLineNote";


function fmtUsd(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}


function PrimerCard({ p }) {
  const t = p.tuition_range_usd;
  return (
    <Link
      to={`/apply/${p.code}`}
      data-testid={`primer-card-${p.code}`}
      className="group border border-stone-200 rounded-lg p-6 bg-white hover:border-stone-500 transition-colors flex flex-col"
    >
      <div className="flex items-baseline gap-3">
        <span className="text-3xl leading-none">{p.flag}</span>
        <div>
          <div className="label-uppercase !text-[10px] text-stone-500">
            {p.application_body}
          </div>
          <div className="font-editorial text-2xl mt-0.5 group-hover:underline underline-offset-4 decoration-stone-400">
            {p.name}
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-stone-600 leading-relaxed">
        {p.one_liner}
      </p>

      <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Intl tuition</div>
          <div className="font-mono mt-0.5">
            {t.low === 0 ? "Free–" : `${fmtUsd(t.low)}–`}
            <br />{fmtUsd(t.high)}<span className="text-stone-500">/yr</span>
          </div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Stay-back</div>
          <div className="font-mono mt-0.5">
            {p.psw_years ? `${p.psw_years} yr` : "—"}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs text-stone-600 group-hover:text-stone-900">
        Read the primer <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}


export default function ApplyPrimersIndex() {
  const [data, setData] = useState({ count: 0, results: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/primers")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="How to apply · Country primers · WhichUniversityAdvisor"
        description="Editorial primers on how to apply to universities in the US, UK, Australia, Canada, Ireland, Netherlands, Germany and France. Deadlines, docs, visa route, tuition ranges and our unique angle for every destination."
        image="/og/apply-primers.png"
        path="/apply"
      />
      <CountryGuidesLineNote testId="primers-country-guides-line" />
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="primers-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">International applicant primers</div>
          <Link to="/applications" data-testid="primers-to-applications"
            className="mt-2 inline-flex items-center gap-1 text-[13px] text-stone-600 hover:text-stone-900 underline underline-offset-4 decoration-stone-300">
            Ready to apply? Go to your applications <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-[1.05]">
            Applying abroad? Start with the country, not the course.
          </h1>
          <p className="mt-6 text-stone-700 max-w-2xl leading-relaxed text-lg">
            The application platform, deadlines, docs and post-study visa rules
            change dramatically by country. Get the paperwork right — then let
            our matching engine pick the course.
          </p>
        </div>

        <aside className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6" data-testid="primers-side-note">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-stone-600" />
            <div className="label-uppercase !text-[10px] text-stone-500">Coverage</div>
          </div>
          <div className="font-editorial text-4xl mt-2">{data.count}</div>
          <div className="text-sm text-stone-600 mt-1">
            country primers, refreshed against official portals every February.
          </div>
        </aside>
      </div>

      <div className="mt-12" data-testid="primers-grid">
        {loading ? (
          <div className="text-sm text-stone-500">Loading primers…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.results.map((p) => <PrimerCard key={p.code} p={p} />)}
          </div>
        )}
      </div>

      <div className="mt-16 border-l-2 border-stone-400 pl-4 py-2 text-sm text-stone-600 max-w-3xl italic">
        Editorial note: Primers reflect the 2026–27 cycle. Immigration rules can
        change — always confirm the specific visa route on the destination
        government website (linked in every primer) before you commit.
      </div>
    </div>
  );
}
